import { FC } from 'react';
import type { QualidadeLote, QualidadePayload } from '@/types/dashboards';
import { fmtData, fmtEspera, fmtUn } from '@/utils/painelFormat';
import PainelCard from '../PainelCard';

const DIA_MS = 86_400_000;

const diasAberta = (entrada: string | null, corte: string): number | null =>
  entrada ? Math.max(0, Math.round((Date.parse(`${corte}T00:00:00Z`) - Date.parse(`${entrada}T00:00:00Z`)) / DIA_MS)) : null;

interface Props {
  fichas: QualidadeLote[];
  payload: QualidadePayload;
}

/**
 * Fichas de CQ pendentes no Datasul com o lote já fora do depósito do CQ — reprocesso e estorno
 * de OP deixam a ficha aberta (o caso do lote 2611267, aberto desde março). Não contam na fila;
 * a lista existe para a Qualidade encerrar no Datasul.
 */
const FichasSemSaldoCard: FC<Props> = ({ fichas, payload }) => {
  if (fichas.length === 0) return null;
  const quantidade = fichas.reduce((acc, l) => acc + l.qt_original, 0);
  return (
    <PainelCard
      testId="qualidade-fichas-sem-saldo"
      titulo={`Fichas abertas sem saldo no CQ (${fmtUn(fichas.length)})`}
      subtitulo="Pendentes no Datasul, mas o lote já saiu do depósito do CQ (reprocesso, OP estornada). Não contam na fila: a Qualidade pode encerrar essas fichas no Datasul."
    >
      <details className="text-sm">
        <summary className="cursor-pointer text-content-secondary">
          Ver as {fmtUn(fichas.length)} fichas ({fmtUn(quantidade)} unidades nas fichas)
        </summary>
        <div className="mt-3 overflow-x-auto -mx-5 px-5">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-theme-border text-left text-2xs uppercase tracking-wide text-content-muted">
                <th className="py-2 pr-3 font-medium">Ficha</th>
                <th className="py-2 pr-3 font-medium">Lote</th>
                <th className="py-2 pr-3 font-medium">SKU</th>
                <th className="py-2 pr-3 font-medium">Depósito</th>
                <th className="py-2 pr-3 font-medium">Entrada no CQ</th>
                <th className="py-2 pr-3 text-right font-medium">Aberta há</th>
                <th className="py-2 text-right font-medium">Quantidade da ficha</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {fichas.map((l) => {
                const dias = diasAberta(l.entrada_cq, payload.corte);
                return (
                  <tr key={l.ficha} className="border-b border-theme-border" data-testid={`ficha-sem-saldo-${l.ficha}`}>
                    <td className="py-2 pr-3">{l.ficha}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <div className="font-medium text-content-primary">{l.lote || '—'}</div>
                      <div className="text-xs text-content-muted">
                        OP {l.op}
                        {l.reprocesso && ' · reprocesso'}
                      </div>
                    </td>
                    <td className="py-2 pr-3 max-w-[260px]">
                      <div className="text-content-primary">{l.codigo_datasul}</div>
                      <div className="truncate text-xs text-content-muted">{payload.itens[l.codigo_datasul]?.descricao || 'Item sem cadastro'}</div>
                    </td>
                    <td className="py-2 pr-3 text-content-secondary">{l.deposito ?? '—'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtData(l.entrada_cq)}</td>
                    <td className="py-2 pr-3 text-right">{dias === null ? '—' : fmtEspera(dias)}</td>
                    <td className="py-2 text-right">{fmtUn(l.qt_original)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </PainelCard>
  );
};

export default FichasSemSaldoCard;
